import {
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
} from '../../../utils/spds/metadata';
import { STRING } from '../../../utils/component-validations';
import { hash } from '@ember/helper';
import Button from './segmented-control/button.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import styles from './segmented-control.module.scss';

const METADATA = {
  component: 'Spds::Action::SegmentedControl',
  description: `
    Used to pick one choice from a linear set of closely related choices, and immediately apply that selection

    ### Contextual component
    The buttons are exposed as a contextual component that can be used within the segmented control multiple times.
    HTML attributes can be set directly on the button to give it a \`value\`, and to set the initially selected button using the \`checked\` attribute.
  `,
  meta: {
    ...pickArgs(ACTION_ARGS, ['onClick']).meta,
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled']).meta,
    ...pickArgs(CONTENT_ARGS, ['iconOnly']).meta,
    alignItems: {
      ...FLEX_ARGS.meta.alignItems,
      defaultValue: 'center',
    },
    gap: {
      ...FLEX_ARGS.meta.gap,
      defaultValue: '2px',
    },
    groupName: {
      type: STRING,
      description: 'Name attribute for the radio group',
      initialValue: 'styleguide-segmented-control-group',
    },
    id: {
      ...COMPONENT_STATE_ARGS.meta.id,
      initialValue: 'styleguide-segmented-control',
    },
    justifyContent: {
      ...FLEX_ARGS.meta.justifyContent,
      defaultValue: 'center',
    },
  },
  name: 'Segmented Control',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-Web-Components?node-id=18486-82323&node-type=INSTANCE',
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/action/segmented-control',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/9684fc-segmented-control',
  },
};

export default class SpdsActionSegmentedControl extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-action-segmented-control';
  buttons = [];
  classesToMapToArgs = ['disabled', 'iconOnly'];
  defaultComponentName = 'Spds::Action::SegmentedControl';
  styles = styles;

  get componentName() {
    return this.args.componentName ?? this.defaultComponentName;
  }

  get groupName() {
    return this.args.groupName ?? this.id;
  }

  registerButton = button => {
    this.buttons.push(button);
  };

  unregisterButton = id => {
    let index = this.buttons.findIndex(button => button.id === id);

    this.buttons.splice(index, 1);
  };

  get alignItems() {
    return (
      this.args.alignItems ?? getDefaultValueFromMetadata(this.constructor.metadata, 'alignItems')
    );
  }

  get gap() {
    return this.args.gap ?? getDefaultValueFromMetadata(this.constructor.metadata, 'gap');
  }

  get justifyContent() {
    return (
      this.args.justifyContent ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'justifyContent')
    );
  }

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @fitContent={{true}}
      @gap={{this.gap}}
      @inline={{true}}
      @justifyContent={{this.justifyContent}}
      class={{this.classes}}
      data-test-spds-action-segmented-control-container
      id={{this.id}}
      role="radiogroup"
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Button=(component
            Button
            disabled=@disabled
            groupName=this.groupName
            onClick=@onClick
            registerComponent=this.registerButton
            unregisterComponent=this.unregisterButton
          )
        )
      }}
    </FlexRow>
  </template>
}

export const ACTION_SEGMENTED_CONTROL_METADATA = SpdsActionSegmentedControl.metadata;
